import React, { useMemo, useState } from "react";
import { CheckCircle2, FileDown, FileText, ShieldCheck } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import SalesAffiliateLayout from "../components/SalesAffiliateLayout";
import SalesAffiliateStatusBanner from "../components/SalesAffiliateStatusBanner";
import { useSalesAffiliateWorkspace } from "../context/SalesAffiliateWorkspaceContext";
import { supabase } from "../lib/supabaseClient";

const ACCEPT_AGREEMENT_FUNCTION_NAME = "sales_affiliate_agreement_acceptance";
const AGREEMENT_DOWNLOAD_PATH =
  "/legal/AI_Fine_Protector_Independent_Sales_Affiliate_Agreement_V3_2_PRODUCTION.odt";

function formatAcceptedAt(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(parsed);
}

export default function SalesAffiliateAgreement() {
  const {
    loading,
    statusLoadFailed,
    errorMessage,
    workspaceData: status,
    configuration,
    lifecycleState,
    refreshWorkspace,
  } = useSalesAffiliateWorkspace();

  const [agreementChecked, setAgreementChecked] = useState(false);
  const [documentPresented, setDocumentPresented] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState("");

  const agreement = status?.agreement || {};
  const agreementComplete = Boolean(agreement.accepted);
  const agreementAvailable = Boolean(agreement.available && agreement.document_url);
  const acceptedAt = useMemo(
    () => formatAcceptedAt(agreement.accepted_at),
    [agreement.accepted_at]
  );

  const handleAcceptAgreement = async () => {
    if (accepting || agreementComplete || !agreementChecked || !documentPresented) {
      return;
    }

    setAcceptError("");
    setAccepting(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        ACCEPT_AGREEMENT_FUNCTION_NAME,
        { body: { accepted: true } }
      );

      if (error) {
        throw new Error(error.message || "Agreement acceptance failed.");
      }

      if (!data?.ok) {
        throw new Error(data?.error || "Agreement acceptance failed.");
      }

      await refreshWorkspace();
      setAgreementChecked(false);
    } catch (err) {
      setAcceptError(err?.message || "Agreement acceptance failed.");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <SalesAffiliateLayout activeSection="agreement" lifecycleState={lifecycleState}>
        <div className="mx-auto max-w-5xl px-6 py-12 text-sm font-semibold text-slate-600">
          Loading agreement…
        </div>
      </SalesAffiliateLayout>
    );
  }

  if (statusLoadFailed) {
    return (
      <SalesAffiliateLayout activeSection="agreement" lifecycleState={lifecycleState}>
        <div className="mx-auto max-w-3xl px-6 py-12">
          <SalesAffiliateStatusBanner
            tone="warning"
            eyebrow="Agreement unavailable"
            title="We could not load your Sales Affiliate Agreement"
          >
            {errorMessage || "Please return to Activation Overview and try again."}
          </SalesAffiliateStatusBanner>
        </div>
      </SalesAffiliateLayout>
    );
  }

  if (!status?.affiliate_exists || !configuration.allowsOnboardingPage) {
    return <Navigate to={configuration.defaultPath} replace />;
  }

  return (
    <SalesAffiliateLayout activeSection="agreement" lifecycleState={lifecycleState}>
      <main className="mx-auto max-w-6xl px-6 py-8 sm:px-8 lg:py-10">
        <div className="flex flex-col gap-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <div className="text-xs font-black uppercase tracking-[0.22em] text-blue-600">
              Required activation document
            </div>
            <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
              Independent Sales Affiliate Agreement
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Review the complete agreement and Exhibit A below. Acceptance is one
              requirement for Sales Affiliate activation; accepting this agreement by
              itself does not activate your account.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm">
            <div className="font-black text-slate-950">Version {agreement.version || "—"}</div>
            <div className="mt-1 text-xs font-semibold text-slate-500">
              {agreement.code || "Sales Affiliate Agreement"}
            </div>
          </div>
        </div>

        {agreementComplete ? (
          lifecycleState === "ACTIVE" ? (
            <div className="mt-6 rounded-3xl border border-emerald-200 bg-emerald-50 p-6 shadow-sm sm:p-7">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 flex-none text-emerald-700" size={24} />
                <div className="min-w-0 flex-1">
                  <div className="text-xl font-black text-emerald-950">Activation complete</div>
                  <p className="mt-1 text-sm leading-6 text-emerald-900">
                    Your Sales Affiliate account is active. All activation requirements have
                    been satisfied.
                  </p>
                  <Link
                    to="/sales-affiliate/dashboard"
                    className="mt-5 inline-flex rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800"
                  >
                    Go to your dashboard
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-3xl border border-emerald-200 bg-emerald-50 p-6 shadow-sm sm:p-7">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 flex-none text-emerald-700" size={24} />
                <div className="min-w-0 flex-1">
                  <div className="text-lg font-black text-emerald-950">Agreement accepted</div>
                  <p className="mt-1 text-sm leading-6 text-emerald-900">
                    You accepted Version {agreement.version || "—"}
                    {acceptedAt ? ` on ${acceptedAt}` : ""}. Continue onboarding to complete
                    your remaining activation requirements.
                  </p>
                  <Link
                    to="/sales-affiliate/onboarding"
                    className="mt-5 inline-flex rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800"
                  >
                    Continue Onboarding
                  </Link>
                </div>
              </div>
            </div>
          )
        ) : null}

        {!agreementAvailable ? (
          <div className="mt-6">
            <SalesAffiliateStatusBanner
              tone="warning"
              eyebrow="Agreement not configured"
              title="The complete required agreement is not yet available"
            >
              Acceptance remains disabled until the exact required agreement version is
              available for review.
            </SalesAffiliateStatusBanner>
          </div>
        ) : (
          <>
            <section className="mt-6 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="text-blue-700" size={20} />
                  <div>
                    <div className="text-sm font-black text-slate-950">Agreement Version {agreement.version}</div>
                    <div className="text-xs font-semibold text-slate-500">Complete agreement and Exhibit A</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={AGREEMENT_DOWNLOAD_PATH}
                    download
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-black text-slate-800 hover:bg-slate-100"
                  >
                    <FileDown size={15} /> Download copy
                  </a>
                </div>
              </div>

              <iframe
                title={`AI Fine Protector Independent Sales Affiliate Agreement Version ${agreement.version || "3.2"}`}
                src={agreement.document_url}
                onLoad={() => setDocumentPresented(true)}
                className="h-[70vh] min-h-[640px] w-full bg-white"
              />
            </section>

            {!agreementComplete ? (
              <section className="mt-6 rounded-3xl border border-blue-200 bg-white p-6 shadow-sm sm:p-7">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 flex-none text-blue-700" size={24} />
                  <div>
                    <h2 className="text-lg font-black text-slate-950">Electronic acceptance</h2>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      The complete agreement must be presented above before acceptance is enabled.
                    </p>
                  </div>
                </div>

                {acceptError ? (
                  <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
                    {acceptError}
                  </div>
                ) : null}

                <label className="mt-6 flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-800">
                  <input
                    type="checkbox"
                    checked={agreementChecked}
                    onChange={(event) => setAgreementChecked(event.target.checked)}
                    disabled={accepting || !documentPresented}
                    className="mt-1 h-4 w-4 flex-none rounded border-slate-300 text-blue-600 focus:ring-blue-600 disabled:cursor-not-allowed"
                  />
                  <span className="font-semibold">
                    I have read the Independent Sales Affiliate Agreement and Exhibit A. By
                    checking this box and clicking I Agree, I electronically sign and agree
                    to be legally bound by them.
                  </span>
                </label>

                {!documentPresented ? (
                  <p className="mt-2 text-xs font-semibold text-slate-500">
                    The acceptance control will become available after the agreement has been presented.
                  </p>
                ) : null}

                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleAcceptAgreement}
                    disabled={!agreementChecked || !documentPresented || accepting}
                    className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {accepting ? "Recording acceptance…" : acceptError ? "Retry acceptance" : "I Agree"}
                  </button>
                  <Link
                    to="/sales-affiliate/onboarding"
                    className="rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-black text-slate-800 hover:bg-slate-50"
                  >
                    Back to Activation Overview
                  </Link>
                </div>
              </section>
            ) : null}
          </>
        )}
      </main>
    </SalesAffiliateLayout>
  );
}
